import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import { getDocumentTypesEndpointIdentifier } from './use-get-document-types-api';

const endpoint = '/ums/v1/cms/document-vault/types';

type TData = void;
type TError = AxiosError;
type TVariables = number;
type Options = Omit<UseMutationOptions<TData, TError, TVariables>, 'mutationFn'>;
type TReturnType = ReturnType<typeof useMutationWrapper<TData, TError, TVariables>>;

export function useDeleteDocumentTypeApi(options?: Options): TReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async (id: TVariables): Promise<TData> => {
    try {
      const { data } = await http.delete(`${endpoint}/${id}`);
      toast({ description: 'Document type deleted successfully', status: 'success' });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: endpoint,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([getDocumentTypesEndpointIdentifier]);
      if (userOnSuccess) userOnSuccess(data, variables, context);
    },
    ...restOptions,
  });
}
